import { ApplicationViewChart } from "fleet/view/device/application-view-chart";
import { ConnectionChartWrapper } from "fleet/view/device/connection-chart-wrapper";
import { formatOsBadge } from "fleet/view/device/table-format";
import { DeviceTags } from "fleet/view/device/tags";
import capitalize from "lodash/capitalize";
import get from "lodash/get";
import moment from "moment";
import PropTypes from "prop-types";
import React from "react";
import { Link } from "react-router-dom";
import { defaultArray } from "redux-store";
import { getDeviceType, hasConnected } from "redux-store/devices/selectors";
import { Fetch } from "ui/fetch";
import { List } from "ui/list";
import { BasicLoader } from "ui/loader";
import { csvStringToArray } from "util/array";
import { utcToLocalFormat } from "util/time";

class DeviceDetails extends React.PureComponent {
  static get propTypes() {
    return {
      applications: PropTypes.object.isRequired,
      data: PropTypes.object,
      hideSidebar: PropTypes.func,
      statsState: PropTypes.object.isRequired,
      storesLoaded: PropTypes.bool.isRequired,
    };
  }

  _getThirdPartySource(thirdPartyId) {
    if (!thirdPartyId) {
      return "";
    }

    // We extract third party source e.g. 'ayuda/testing' by looking for the '/'
    const source = thirdPartyId.substring(0, thirdPartyId.indexOf("/"));
    return capitalize(source);
  }

  _getStrategyStartTime(startTime) {
    if (!startTime) {
      return null;
    }

    const startMoment = moment.utc(startTime);
    const fromNow = startMoment.fromNow();
    return (
      <span>
        <br />
        The strategy has been running since {fromNow}
      </span>
    );
  }

  renderStrategy(stats) {
    const strategy = get(stats, "strategy", {});
    const { startTime, name } = strategy;

    return (
      <div className="text-muted">
        {name || "Real-time strategy info not available"}
        {this._getStrategyStartTime(startTime)}
      </div>
    );
  }

  render() {
    const { applications, statsState, storesLoaded, data: device } = this.props;
    const {
      tags,
      current_version: playerVersion,
      device_id: deviceId,
      ip_addresses: ipAddresses_,
      mac_addresses: macAddresses_,
      network_id: networkId,
      network_name: networkName,
      os_platform: osId,
      thirdparty_id: thirdPartyId,
    } = device;
    const { stats } = statsState;

    const statusLoaded = statsState.deviceId === deviceId;

    const ipAddresses = csvStringToArray({ sort: true }, ipAddresses_);
    const macAddresses = csvStringToArray({ sort: true }, macAddresses_);

    const connectionData = get(stats, "stats.connection", defaultArray);
    const deviceType = getDeviceType(device);
    const thirdPartySource = this._getThirdPartySource(thirdPartyId);
    const lastOnlineDate = get(stats, "last_online");
    const isShell = !hasConnected(device);
    const viewData = get(stats, "stats.view", defaultArray);
    const lastOnline = utcToLocalFormat(
      lastOnlineDate,
      "MMM Do, YYYY h:mm a",
      "more than 2 months ago",
    );

    return (
      <div className="device-details">
        <Fetch.DeviceStats deviceId={deviceId} />

        <section className="device-id">
          <h4>Device ID</h4>
          <div className="text-muted text-monospace">
            <Link to={`/devices/${deviceId}`}>{deviceId}</Link>
          </div>
        </section>

        <section className="network">
          <h4>Network</h4>
          <div className="text-muted">
            <Link
              to={{
                pathname: "/redirect",
                search: `to=/networks/${networkId}`,
              }}
            >
              {networkName}
            </Link>
          </div>
        </section>

        <section className="connection">
          <h4>Connection Status (past 24 hours)</h4>
          {statusLoaded ? (
            <ConnectionChartWrapper
              connectionData={connectionData}
              isShell={isShell}
              lastOnline={lastOnline}
            />
          ) : (
            <BasicLoader className="text-muted" />
          )}
        </section>

        <section className="strategy">
          <h4>Current Strategy</h4>
          {statusLoaded ? (
            this.renderStrategy(stats)
          ) : (
            <BasicLoader className="text-muted" />
          )}
        </section>

        <section className="display-status">
          <h4>Display Status</h4>
          {storesLoaded && statusLoaded ? (
            <ApplicationViewChart applications={applications} data={viewData} />
          ) : (
            <BasicLoader className="text-muted" />
          )}
        </section>

        <section className="device-type">
          <h4>Device Type</h4>
          <div className="text-muted">
            {deviceType} {playerVersion} {thirdPartySource}
          </div>
        </section>

        <section className="operating-system">
          <h4>Operating System</h4>
          <div>{osId ? formatOsBadge(osId) : "No info"}</div>
        </section>

        <section className="ip-addresses">
          <h4>IP Addresses</h4>
          <List
            className="list-unstyled text-muted"
            items={ipAddresses}
            emptyValue="No IP addresses"
            itemClass="text-monospace"
            hover
            striped
          />
        </section>

        <section className="mac-addresses">
          <h4>MAC Addresses</h4>
          <List
            className="list-unstyled text-muted"
            items={macAddresses}
            emptyValue="No MAC addresses"
            itemClass="text-monospace"
            hover
            striped
          />
        </section>

        <section className="tags">
          <h4>Tags</h4>
          <div className="text-muted">
            <DeviceTags tags={tags} emptyValue="No tags" hideSystemTags />
          </div>
        </section>
      </div>
    );
  }
}

export { DeviceDetails };
